import { SignalRService } from 'features/SignalR/service/SignalRService'
import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import {
  fetchReceivedProjectQuotationCount,
  fetchRfqInboxCount,
  QuotationExchangeInvalidation,
  quotationExchangeActions,
} from './quotationExchange.store'

const eventName = 'onQuotationExchangeUpdated'

export const QuotationExchangeCoordinator = () => {
  const dispatch = useAppDispatch()
  const partyId = useAppSelector((state) => state.user?.organizationContext?.id)
  const projectId = useAppSelector((state) => state.project.activeProject?.id)
  const connectionStatus = useAppSelector((state) => state.connection.status)
  const previousConnectionStatus = useRef(connectionStatus)
  const projectIdRef = useRef(projectId)
  projectIdRef.current = projectId

  useEffect(() => {
    if (!partyId) {
      dispatch(quotationExchangeActions.clear())
      return undefined
    }

    let disposed = false
    let hub: Awaited<ReturnType<typeof SignalRService.GetHub>> | undefined
    const groupName = `${partyId}_quotation_exchange`
    const reconcile = () => {
      void dispatch(fetchRfqInboxCount(partyId))
      if (projectIdRef.current) {
        void dispatch(
          fetchReceivedProjectQuotationCount({
            partyId,
            projectId: projectIdRef.current,
          }),
        )
      }
    }
    const handleUpdate = (payload: QuotationExchangeInvalidation) => {
      if (!payload || payload.partyId !== partyId) return
      dispatch(quotationExchangeActions.invalidated(payload))
      if (payload.kind === 'RequestForQuotation') {
        void dispatch(fetchRfqInboxCount(partyId))
      } else if (
        payload.kind === 'QuotationResponse' &&
        payload.projectId === projectIdRef.current
      ) {
        void dispatch(
          fetchReceivedProjectQuotationCount({
            partyId,
            projectId: payload.projectId,
          }),
        )
      }
    }

    dispatch(quotationExchangeActions.partyChanged(partyId))
    reconcile()
    void SignalRService.GetHub().then(async (connectedHub) => {
      if (disposed) return
      hub = connectedHub
      hub.registerHandler(eventName, handleUpdate)
      await hub.JoinGroup(groupName, reconcile)
    })

    return () => {
      disposed = true
      if (!hub) return
      hub.unregister(eventName, handleUpdate)
      void hub.LeaveGroup(groupName, () => undefined)
    }
  }, [dispatch, partyId])

  useEffect(() => {
    if (partyId && projectId) {
      void dispatch(fetchReceivedProjectQuotationCount({ partyId, projectId }))
    }
  }, [dispatch, partyId, projectId])

  useEffect(() => {
    if (
      partyId &&
      connectionStatus === 'online' &&
      previousConnectionStatus.current !== 'online'
    ) {
      void dispatch(fetchRfqInboxCount(partyId))
      if (projectId) {
        void dispatch(
          fetchReceivedProjectQuotationCount({ partyId, projectId }),
        )
      }
    }
    previousConnectionStatus.current = connectionStatus
  }, [connectionStatus, dispatch, partyId, projectId])

  return null
}
